import type { BookTimelineLayout } from "@/lib/timeline/book/BookLayout";
import { isPartLocationRun } from "../LocationRun/anchor";
import { allLocationRuns } from "../LocationRun/catalog";
import {
  branchForSiteRun,
  forkTravelerFollowActive,
  siteRunFollowsCastColumn,
} from "../LocationRun/siteAnchor";
import type { LocationRun } from "../LocationRun/runs";
import { branchIdForPartRun, partRunPhase } from "../../rail/PartRunPhase/phase";

/** The cast is already in this part as the rail continues, as across chapters. */
function partCarriesOnFromLastRail(
  layout: BookTimelineLayout,
  run: LocationRun,
): boolean {
  const branchId = branchIdForPartRun(run);
  const branch = layout.rail.branches.find((item) => item.id === branchId);
  if (branch === undefined || run.startMs !== branch.startMs) {
    return false;
  }
  const continues = layout.rail.branches.some((item) => (
    item.endMs === branch.startMs && item.label === branch.label
    && (item.lane ?? 0) === (branch.lane ?? 0)
  ));
  return continues && layout.rail.stays.some((stay) => (
    stay.placeLabel === run.locationLabel
    && stay.startMs < branch.startMs && stay.endMs >= branch.startMs
  ));
}

/** Playhead-time rules only; camera zoom/pan never affects this. */
export function locationRunRelevantAtPlayhead(
  layout: BookTimelineLayout,
  run: LocationRun,
  playheadMs: number,
): boolean {
  if (isPartLocationRun(run)) {
    const phase = partRunPhase(layout.rail, run, playheadMs);
    if (phase.kind === "upcoming") {
      return !partCarriesOnFromLastRail(layout, run);
    }
    if (phase.kind === "active") {
      return true;
    }
    return forkTravelerFollowActive(layout.rail, run, playheadMs);
  }
  const branch = branchForSiteRun(run, layout.rail);
  if (siteRunFollowsCastColumn(layout.rail, run, playheadMs)) {
    // Every visit on a rail follows the cast, so only the first one draws a box.
    return branch === undefined
      || (branch.lane ?? 0) !== 0
      || run.startMs <= branch.startMs;
  }
  if (branch === undefined) {
    return false;
  }
  if ((branch.lane ?? 0) === 0) {
    // The next site waits where its rail begins. A revisit inside a rail adds nothing,
    // and neither does a rail that just carries on the same site, as across chapters.
    const continues = layout.rail.branches.some((item) => (
      item.endMs === branch.startMs && item.label === branch.label && (item.lane ?? 0) === 0
    ));
    return playheadMs < run.startMs && run.startMs === branch.startMs && !continues;
  }
  if (playheadMs >= branch.endMs) {
    return forkTravelerFollowActive(layout.rail, run, playheadMs);
  }
  return playheadMs < run.endMs;
}

export function filterLocationVisibilityAtPlayhead(
  layout: BookTimelineLayout,
  playheadMs: number,
  visibility: ReadonlyMap<string, boolean>,
): Map<string, boolean> {
  const out = new Map<string, boolean>();
  for (const run of allLocationRuns(layout)) {
    const inGroup = visibility.get(run.runId) ?? true;
    out.set(
      run.runId,
      inGroup && locationRunRelevantAtPlayhead(layout, run, playheadMs),
    );
  }
  return out;
}
